import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';

// Helper function to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  category?: string;
  custom_label_0?: string;
  image_url?: string;
  url?: string;
  status: 'active' | 'inactive';
  whatsapp_product_id?: string;
  retailer_id?: string; // This is the SKU/content_id needed for product messages
  created_at: string;
  updated_at: string;
}

export interface CatalogInfo {
  id: string;
  name: string;
  business_account_id: string;
  whatsapp_catalog_id: string;
  status: 'connected' | 'disconnected' | 'syncing';
  last_sync_at?: string;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface AvailableCatalog {
  id: string;
  name: string;
  product_count: number;
  status: string;
  whatsapp_catalog_id?: string;
}

export interface CommerceStats {
  total_revenue: number;
  total_orders: number;
  total_products: number;
  avg_order_value: number;
  revenue_change: number;
  orders_change: number;
  products_change: number;
  aov_change: number;
  top_products: Array<{
    productName: string;
    totalSold: number;
    totalRevenue: number;
  }>;
  sales_history: Array<{
    name: string;
    date: string;
    sales: number;
    orders: number;
  }>;
  sources?: {
    native: { revenue: number; orders: number; products: number };
    shopify: { revenue: number; orders: number; products: number };
    woocommerce: { revenue: number; orders: number; products: number };
  };
}

export interface Order {
  id: string;
  order_number: string;
  source: 'native' | 'shopify' | 'woocommerce';
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  total_amount: number;
  currency: string;
  status: string;
  payment_status: string;
  created_at: string;
  items_count: number;
  source_id?: string;
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: { orderId: string; status: string; syncPetpooja?: boolean; meta_data?: Record<string, any> }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders/${params.orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          status: params.status,
          syncPetpooja: params.syncPetpooja ?? true,
          ...(params.meta_data ? { meta_data: params.meta_data } : {}),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to update order');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
    },
  });
}

/**
 * Server hard cap on `GET /commerce/orders?limit=` — `CommerceController.getOrders`
 * clamps with `Math.min(100, ...)`, so asking for more silently returns 100.
 * Exported because the paging loop below has to know the real ceiling: a wrong
 * constant here would make it think a full page was a short one and stop early.
 */
export const ORDERS_MAX_PAGE_SIZE = 100;

/**
 * Safety stop for `useAllOrders`. 25 pages × 100 = 2,500 orders held in memory
 * and re-fetched on every poll. Past that the loop gives up and reports
 * `truncated`, which the caller must surface — silently stopping is the exact
 * bug this hook exists to fix, one order of magnitude further out.
 */
export const ORDERS_MAX_PAGES = 25;

export interface OrdersPage {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
}

/**
 * One page of `GET /commerce/orders`. Shared by `useOrders` (one page) and
 * `useAllOrders` (every page) so the URL, the error shape and the
 * `total`-vs-`pagination.total` fallback are stated once.
 */
async function fetchOrdersPage(
  businessAccountId: string,
  filters?: {
    search?: string;
    status?: string;
    source?: string;
    page?: number;
    limit?: number;
  },
): Promise<OrdersPage> {
  const params = new URLSearchParams({
    businessAccountId,
    ...(filters?.search && { search: filters.search }),
    ...(filters?.status && { status: filters.status }),
    ...(filters?.source && { source: filters.source }),
    page: (filters?.page || 1).toString(),
    limit: (filters?.limit || 20).toString(),
  });

  const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch orders');
  }
  const data = await response.json();
  const rows: Order[] = Array.isArray(data.data) ? data.data : [];
  const totalFromPagination =
    typeof data?.pagination?.total === 'number' ? data.pagination.total :
      (typeof data?.total === 'number' ? data.total : null);
  return {
    orders: rows,
    total: totalFromPagination ?? rows.length,
    page: data.page || 1,
    limit: data.limit || 20,
  };
}

// Hook to fetch unified orders
export function useOrders(
  filters?: {
    search?: string;
    status?: string;
    source?: string;
    page?: number;
    limit?: number;
  },
  // Real gating. Callers used to "switch this query off" by passing
  // `filters: undefined`, which never stopped the fetch — it only dropped the
  // caller's page size, so the request went out at the server default of 20
  // and under a different React Query key.
  options?: { enabled?: boolean },
) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<OrdersPage>({
    queryKey: ['orders', selectedAccountId, filters],
    queryFn: async () => {
      if (!selectedAccountId) return { orders: [], total: 0, page: 1, limit: 20 };
      return fetchOrdersPage(selectedAccountId, filters);
    },
    enabled: !!selectedAccountId && options?.enabled !== false,
    staleTime: 0,
    refetchInterval: 15000, // Poll every 15s so rider accept/status changes reflect without manual refresh
  });
}

export interface AllOrders {
  orders: Order[];
  /** The server's `COUNT(*)` for the account — what a tab badge should show. */
  total: number;
  /** True when `ORDERS_MAX_PAGES` stopped the loop before `total` was reached. */
  truncated: boolean;
}

/**
 * EVERY order for the account, walked page by page.
 *
 * 🔴 WHY THIS EXISTS. `useOrders({ page: 1, limit: 100 })` returns ONE page but
 * a `total` counted over the whole table. Commerce V2 fed the tab badge from
 * `total` (287) and the table from that single page (100), then filtered,
 * searched and counted filter facets over those 100 rows — so 187 orders were
 * not merely off-screen, they were unreachable: unsearchable, unfilterable, and
 * invisible to the facet counts (the "WhatsApp 99 / Instagram 1" that added up
 * to the page size rather than to the account).
 *
 * Everything downstream stays client-side, which is what keeps search, all six
 * filters, the facet counts, bulk selection and the Overview tiles agreeing
 * with each other and with the badge. The cost is honest and bounded: one
 * request per 100 orders, `ORDERS_MAX_PAGES` hard stop, `truncated` reported to
 * the caller rather than swallowed.
 *
 * The poll is 30s, not `useOrders`'s 15s: each tick now re-reads every page, so
 * the interval is doubled to keep the request RATE in the same place for a
 * typical account instead of quietly multiplying it by the page count.
 */
export function useAllOrders(options?: { enabled?: boolean }) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<AllOrders>({
    // Under the 'orders' prefix on purpose — `useUpdateOrderStatus` invalidates
    // ['orders'], and this list has to repaint on a status write like any other.
    queryKey: ['orders', selectedAccountId, 'all'],
    queryFn: async () => {
      if (!selectedAccountId) return { orders: [], total: 0, truncated: false };

      const first = await fetchOrdersPage(selectedAccountId, {
        page: 1,
        limit: ORDERS_MAX_PAGE_SIZE,
      });

      const collected = [...first.orders];
      const total = Math.max(first.total, collected.length);

      // Trust neither `total` nor the row count alone: stop when the account is
      // exhausted (short page), when `total` is reached, or at the hard cap.
      let page = 1;
      let exhausted = first.orders.length < ORDERS_MAX_PAGE_SIZE;
      while (!exhausted && collected.length < total && page < ORDERS_MAX_PAGES) {
        page += 1;
        const next = await fetchOrdersPage(selectedAccountId, {
          page,
          limit: ORDERS_MAX_PAGE_SIZE,
        });
        if (next.orders.length === 0) break;
        collected.push(...next.orders);
        exhausted = next.orders.length < ORDERS_MAX_PAGE_SIZE;
      }

      // De-dupe by id. Rows shift between pages when an order is created while
      // the loop is running (the list is `ORDER BY created_at DESC`), which can
      // otherwise hand React the same key twice.
      const seen = new Set<string>();
      const orders = collected.filter((o) => {
        const id = String((o as { id?: unknown }).id ?? '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      return {
        orders,
        total: Math.max(total, orders.length),
        truncated: !exhausted && orders.length < total,
      };
    },
    enabled: !!selectedAccountId && options?.enabled !== false,
    staleTime: 0,
    refetchInterval: 30000,
  });
}


// Hook to fetch available catalogs from Meta
export function useAvailableCatalogs(options?: { sync?: boolean }) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<AvailableCatalog[]>({
    queryKey: ['available-catalogs', selectedAccountId, options?.sync],
    queryFn: async () => {
      if (!selectedAccountId) return [];

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      if (options?.sync) {
        params.append('sync', 'true');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error('Failed to fetch available catalogs');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch catalog information
export function useCatalogInfo() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<CatalogInfo | null>({
    queryKey: ['catalog-info', selectedAccountId],
    queryFn: async () => {
      if (!selectedAccountId) return null;


      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/info?businessAccountId=${selectedAccountId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch catalog info');
      }
      const data = await response.json();
      return data.data || null; // Extract data from response
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch products
export function useProducts(filters?: {
  search?: string;
  category?: string;
  status?: string;
  page?: number;
  limit?: number;
  catalogId?: string; // Add catalogId param
  enabled?: boolean; // Caller opt-out (e.g. when the WhatsApp-catalog tab isn't active)
}) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products', selectedAccountId, filters],
    queryFn: async () => {
      if (!selectedAccountId) return { products: [], total: 0, page: 1, limit: 20 };

      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.catalogId && { catalogId: filters.catalogId }), // Pass catalogId
        page: (filters?.page || 1).toString(),
        limit: (filters?.limit || 20).toString(),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page: 1, limit: 20 };
    },
    enabled: !!selectedAccountId && filters?.enabled !== false,
    staleTime: 0, // Disable caching
  });
}

/**
 * Page-accumulating variant of `useProducts`, for surfaces that load more on
 * scroll instead of paging (the inbox Send Product drawer).
 *
 * Deliberately a SEPARATE hook rather than an option on `useProducts`: that one
 * has ~10 callers that expect a single page and a `{ products, total }` shape,
 * and `useInfiniteQuery` returns `{ pages: [...] }`. It also uses its own query
 * key, so the two never share a cache entry.
 */
export function useInfiniteProducts(filters?: {
  search?: string;
  category?: string;
  status?: string;
  limit?: number;
  catalogId?: string;
  enabled?: boolean;
}) {
  const { selectedAccountId } = useBusinessAccountContext();
  const limit = filters?.limit || 20;

  return useInfiniteQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    // `search`/`category`/`catalogId` are part of the key, so changing a filter
    // starts a FRESH infinite query at page 1 — no manual reset needed.
    queryKey: ['products-infinite', selectedAccountId, { ...filters, limit }],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      if (!selectedAccountId) return { products: [], total: 0, page: 1, limit };

      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.catalogId && { catalogId: filters.catalogId }),
        page: String(pageParam),
        limit: String(limit),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page: 1, limit };
    },
    getNextPageParam: (lastPage, allPages) => {
      // An empty page ends the list even when `total` disagrees. Without this a
      // server whose count is stale (or larger than what it will actually
      // return) would keep answering "there is more", and every scroll to the
      // bottom would fire another request forever.
      if (!lastPage?.products?.length) return undefined;
      const loaded = allPages.reduce((n, p) => n + (p?.products?.length || 0), 0);
      return loaded < (lastPage.total || 0) ? allPages.length + 1 : undefined;
    },
    enabled: !!selectedAccountId && filters?.enabled !== false,
    staleTime: 0,
  });
}

// Hook to fetch commerce statistics
export function useCommerceStats(options?: { from?: Date; to?: Date }) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<CommerceStats>({
    queryKey: ['commerce-stats', selectedAccountId, options?.from, options?.to],
    queryFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      // Send dates as YYYY-MM-DD strings to avoid timezone shift
      // (toISOString converts to UTC which shifts the date in IST/other timezones)
      if (options?.from) {
        const d = options.from;
        params.append('startDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      if (options?.to) {
        const d = options.to;
        params.append('endDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/stats?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch commerce stats');
      }
      const data = await response.json();
      return data.data || {
        total_revenue: 0,
        total_orders: 0,
        total_products: 0,
        avg_order_value: 0,
        revenue_change: 0,
        products_change: 0,
        aov_change: 0,
        top_products: [],
        sales_history: []
      };
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to setup catalog
export function useSetupCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (catalogId: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          catalogId,
          businessAccountId: selectedAccountId,
        }),
      });

      if (!response.ok) {
        // Preserve the backend's code/details/statusCode so the friendly-error
        // layer (showApiError) can surface the specific reason, not the generic label.
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to setup catalog');
      }

      return response.json();
    },
    // Optimistic update for instant UI feedback
    onMutate: async (catalogId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['catalog-info', selectedAccountId] });
      await queryClient.cancelQueries({ queryKey: ['available-catalogs', selectedAccountId] });

      // Snapshot the previous value
      const previousCatalogInfo = queryClient.getQueryData(['catalog-info', selectedAccountId]);
      const availableCatalogs = queryClient.getQueryData<AvailableCatalog[]>(['available-catalogs', selectedAccountId]);

      // Optimistically update to the new catalog
      if (availableCatalogs) {
        const selectedCatalog = availableCatalogs.find(cat => cat.id === catalogId);
        if (selectedCatalog) {
          queryClient.setQueryData(['catalog-info', selectedAccountId], {
            id: catalogId,
            name: selectedCatalog.name,
            business_account_id: selectedAccountId,
            whatsapp_catalog_id: catalogId,
            status: 'connected',
            product_count: selectedCatalog.product_count,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }

      // Return context with previous value for rollback
      return { previousCatalogInfo };
    },
    // On error, rollback to previous value
    onError: (err, catalogId, context) => {
      if (context?.previousCatalogInfo) {
        queryClient.setQueryData(['catalog-info', selectedAccountId], context.previousCatalogInfo);
      }
    },
    // Always refetch after success or error to ensure data is in sync
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs'] });
    },
  });
}



// Hook to create a new catalog
export function useCreateCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (name: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate available catalogs to refresh the list
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
    },
  });
}

// Hook to sync catalog
export function useSyncCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          catalogId, // Pass catalogId if provided
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to sync catalog');
      }

      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });

      // Return the response data so the component can show the message
      return data;
    },
  });
}

/**
 * Re-read ONE product from the Meta catalog.
 *
 * 🔴 NOT `useSyncCatalog` WITH AN ARGUMENT. That mutation posts to
 * `/commerce/catalog/sync`, which QUEUES a background sweep of the whole
 * catalog and holds a sync lock — the Products row menu used it, so "Sync now"
 * on one row re-read every product and still could not report the result.
 * `/commerce/products/:id/sync` is one Graph read and answers synchronously,
 * which is what lets the caller show the refreshed row immediately.
 *
 * `found: false` is a SUCCESS: the product is genuinely no longer in the Meta
 * catalog. Nothing local is deleted (deletion tracking belongs to the full
 * sweep, which can tell an absence from a failed read), so the caller reports
 * it rather than treating it as an error.
 */
export function useSyncProductFromMeta() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (metaProductId: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/commerce/products/${encodeURIComponent(metaProductId)}/sync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ businessAccountId: selectedAccountId }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult(
          { ...error, statusCode: response.status },
          'Failed to sync this product from Meta',
        );
      }

      return (await response.json()) as {
        success: boolean;
        found: boolean;
        created?: boolean;
        updated?: boolean;
        message?: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Hook to re-link existing catalog to WABA (repairs broken linkage without a full reconnect)
export function useRelinkCatalogToWaba() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/relink-waba`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: selectedAccountId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to re-link catalog to WhatsApp');
      }

      return response.json();
    },
  });
}

// Hook to delete catalog
export function useDeleteCatalog() {
  const { selectedAccountId } = useBusinessAccountContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      // If catalogId provided, add to query string
      let url = `${API_CONFIG.BASE_URL}/commerce/catalog?businessAccountId=${selectedAccountId}`;
      if (catalogId) {
        url += `&catalogId=${catalogId}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch commerce data
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
    },
  });
}

// Hook to rename catalog
export function useRenameCatalog() {
  const { selectedAccountId } = useBusinessAccountContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ catalogId, name }: { catalogId: string; name: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/${catalogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to rename catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
    },
  });
}

// Hook to fetch product sets
export function useProductSets(catalogId?: string) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{ id: string; name: string; product_count: number }[]>({
    queryKey: ['product-sets', selectedAccountId, catalogId],
    queryFn: async () => {
      if (!selectedAccountId) return [];

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      if (catalogId) params.append('catalogId', catalogId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch product sets');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch products by product set
export function useProductsBySet(setId?: string, opts?: { page?: number; limit?: number }) {
  const { selectedAccountId } = useBusinessAccountContext();
  const page = opts?.page ?? 1;
  const limit = opts?.limit ?? 20;

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products-by-set', selectedAccountId, setId, page, limit],
    queryFn: async () => {
      if (!selectedAccountId || !setId) return { products: [], total: 0, page, limit };
      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        page: String(page),
        limit: String(limit),
      });
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets/${setId}/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products by set');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page, limit };
    },
    enabled: !!selectedAccountId && !!setId,
    staleTime: 0, // Disable caching
  });
}

// Hook to bulk import products from Excel/CSV
export function useBulkImportProducts() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: {
      products: Array<{
        name: string;
        description: string;
        price: number;
        currency: string;
        category: string;
        availability: string;
        condition: string;
        brand?: string;
        url?: string;
        image_url: string | string[];
        retailer_id?: string;
      }>;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          products: data.products,
          catalogId: data.catalogId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to bulk import products');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to add a new product
export function useAddProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (productData: {
      name: string;
      description: string;
      price: number;
      currency: string;
      category: string;
      availability: string;
      condition: string;
      brand?: string;
      url?: string;
      // The catalog's own product id (Meta calls it retailer_id, the UI calls
      // it SKU). Declared by POST /commerce/products, read by
      // MetaCommerceService.createProduct, and the key the controller's
      // duplicate-retailer_id branch upserts on — so sending it turns a repeat
      // submit into an UPDATE (`upserted: true`) instead of a 400. It was
      // missing from this type only, which is why the create form could not
      // offer a SKU field; the body below spreads productData, so nothing else
      // had to change. The bulk-import path already sends it.
      retailer_id?: string;
      image_url?: string[];
      sets?: string[];
      newSetName?: string;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...productData,
          businessAccountId: selectedAccountId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create product');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch products
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to delete a product
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: { whatsappProductId: string; catalogId?: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      let url = `${API_CONFIG.BASE_URL}/commerce/products/${encodeURIComponent(params.whatsappProductId)}?businessAccountId=${selectedAccountId}`;
      if (params.catalogId) {
        url += `&catalogId=${encodeURIComponent(params.catalogId)}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to fetch business profile settings
export function useBusinessProfile() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{ is_cart_enabled: boolean; is_catalog_visible: boolean }>({
    queryKey: ['business-profile-settings', selectedAccountId],
    queryFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile?businessAccountId=${selectedAccountId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch business profile settings');
      }

      const data = await response.json();
      return data.data;
    },
    enabled: !!selectedAccountId,
  });
}

// Hook to update business profile settings
export function useUpdateBusinessProfile() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (settings: { is_cart_enabled?: boolean; is_catalog_visible?: boolean }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          settings,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update settings');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profile-settings', selectedAccountId] });
    },
  });
}

// Hook to update a product
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: {
      whatsappProductId: string;
      updates: any;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/${params.whatsappProductId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: selectedAccountId, ...params.updates, catalogId: params.catalogId })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId] });
    }
  });
}

// Hook to create a product set
export function useCreateProductSet() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (params: { name: string; catalogId?: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name: params.name,
          catalogId: params.catalogId
        })
      });
      if (!response.ok) throw new Error('Failed to create set');
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId, variables.catalogId] });
      // Also invalidate default query just in case
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId] });
    },
  });
}
